'use client';

import { useState, useCallback } from 'react';
import { useTranslation } from 'next-i18next';
import { Button, LinkStyled, TailwindSpinner } from '@components/common';
import { useSwrApi, ApiHeaderInfoOk } from '@hooks/useSwrApi';
import { MY_BESTIE_PRICE_EUR } from '@components/features/modals/buy-my-bestie/constants';
import { CheckCircleIcon } from '@heroicons/react/24/solid';
import { showBuyMyBestieModal } from '@components/features/modals/buy-my-bestie/BuyMyBestieModal';
import NiceModal from '@ebay/nice-modal-react';
import { PersonalDataModal } from '@components/templates/job/apply/PersonalDataModal';
import { useSwrResumePersonalData } from '@hooks/useSwrApi/endpoints/resume';
import { useSwrUserPhoneNumber } from '@hooks/useSwrApi/endpoints/user/useSwrUserPhoneNumber';
import {
  fetcher, isPhoneNumberInfoOk,
} from '@utils/data-fetching';
import type { ApiUserPhoneInfo } from '@type/v1-api-types/user';

interface MyBestieLandingProps {
  locale: Locale;
  isAuthenticated: boolean;
}

const BENEFITS_KEYS = [
  'personal.agent.benefits.1',
  'personal.agent.benefits.2',
  'personal.agent.benefits.3',
  'personal.agent.benefits.4',
  'personal.agent.benefits.5',
] as const;

export const MyBestieLanding = ({ locale, isAuthenticated }: MyBestieLandingProps) => {
  const { t } = useTranslation('common');
  const [purchaseCompleted, setPurchaseCompleted] = useState(false);

  const { data: headerInfo, mutate: mutateHeaderInfo } = useSwrApi<ApiHeaderInfoOk>({
    url: '/v1/header-info',
    shouldFetch: isAuthenticated,
    locale,
    apiOptions: { revalidateOnMount: true },
  });

  const { data: personalData, mutate: mutatePersonalData } = useSwrResumePersonalData({
    locale,
    shouldFetch: isAuthenticated,
  });

  const { data: phoneData } = useSwrUserPhoneNumber({
    locale,
    shouldFetch: isAuthenticated,
  });

  const headerInfoReady = !isAuthenticated || !!headerInfo;
  const myBestieEnabled = purchaseCompleted || !!headerInfo?.myBestieEnabled;

  const handlePurchaseSuccess = useCallback(() => {
    setPurchaseCompleted(true);
    void mutateHeaderInfo();
  }, [mutateHeaderInfo]);

  const openBuyModal = useCallback(() => {
    showBuyMyBestieModal(handlePurchaseSuccess);
  }, [handlePurchaseSuccess]);

  const handleActivateClick = useCallback(async () => {
    const hasFirstName = !!personalData?.firstName;
    const hasLastName = !!personalData?.lastName;
    const phoneNumber = phoneData?.data?.number;
    const phoneCountryCode = phoneData?.data?.country?.data?.countryCode;
    const hasPhone = !!phoneNumber && !!phoneCountryCode;

    let phoneConfirmed = false;
    if (hasPhone) {
      const phoneInfo = await fetcher<ApiUserPhoneInfo>(
        '/v1/user-phone/info',
        locale,
        { method: 'POST', payload: { number: `+${phoneCountryCode}${phoneNumber}` }, stringifyPayload: true },
      );
      phoneConfirmed = isPhoneNumberInfoOk(phoneInfo) && phoneInfo.isConfirmed;
    }

    if (!hasFirstName || !hasLastName || !hasPhone || !phoneConfirmed) {
      void NiceModal.show(PersonalDataModal, {
        locale,
        onUpdateSuccess: () => {
          void mutateHeaderInfo();
          void mutatePersonalData();
          openBuyModal();
        },
      });
      return;
    }

    openBuyModal();
  }, [personalData, phoneData, locale, mutateHeaderInfo, mutatePersonalData, openBuyModal]);

  return (
    <div className="flex min-h-full grow flex-col items-center justify-end bg-surface-100 pb-16 pt-6 lg:pb-8 lg:pt-16">
      <div className="flex w-full max-w-3xl flex-col items-center gap-6 px-4 text-center sm:px-6 lg:px-0">
        <h1 className="w-full text-4xl font-bold leading-snug sm:text-5xl sm:leading-snug lg:text-[72px] lg:leading-snug">
          {t('personal.agent.landing.title')}
        </h1>

        <p className="w-full max-w-2xl text-base text-ink/75 sm:text-lg lg:text-2xl">
          {t('personal.agent.landing.subtitle').replace('{amount}', MY_BESTIE_PRICE_EUR.toString())}
        </p>

        <ul className="flex w-full max-w-2xl flex-col gap-3 text-left text-base sm:text-lg">
          {BENEFITS_KEYS.map((key) => (
            <li key={key} className="flex items-start gap-3">
              <CheckCircleIcon className="mt-0.5 size-6 shrink-0 text-secondary" />
              <span>{t(key)}</span>
            </li>
          ))}
        </ul>

        <p className="w-full max-w-2xl text-base text-ink/75 sm:text-lg">
          {t('personal.agent.landing.description')}
        </p>

        <div className="flex w-full max-w-2xl justify-center pb-8 pt-2">
          {!headerInfoReady && (
            <TailwindSpinner className="size-8 text-primary" />
          )}
          {headerInfoReady && myBestieEnabled && (
            <Button
              type="button"
              styling="solid"
              color="secondary-ink"
              size="lg"
              rounding="2xl"
              onClick={() => {}}
            >
              {t('personal.agent.already.activated')}
            </Button>
          )}
          {headerInfoReady && !myBestieEnabled && !isAuthenticated && (
            <LinkStyled
              href={`/login?targetPath=${encodeURIComponent(`/${locale}/mc/my-bestie`)}`}
              styling="solid"
              color="secondary-ink"
              size="lg"
              rounding="2xl"
            >
              {t('mc.personal.agent.activate').replace('{amount}', MY_BESTIE_PRICE_EUR.toString())}
            </LinkStyled>
          )}
          {headerInfoReady && !myBestieEnabled && isAuthenticated && (
            <Button
              type="button"
              styling="solid"
              color="secondary-ink"
              size="lg"
              rounding="2xl"
              onClick={() => { void handleActivateClick(); }}
            >
              {t('mc.personal.agent.activate').replace('{amount}', MY_BESTIE_PRICE_EUR.toString())}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};
